/**
 * features/admin/BulkUserCreation.tsx
 * ------------------------------------------------------------------
 * Bulk create Firebase Auth accounts for teachers.
 *
 * Username: surname@lkc.ac.bw (lowercase, trimmed).
 * If duplicate surname exists, falls back to surname+firstInitial@lkc.ac.bw.
 * Auto-generates a temporary password (LKC + 6 random chars + !).
 * Sends welcome email with credentials to the teacher's profile email.
 * Advises password reset on first login.
 *
 * Uses the createBulkUsers Cloud Function for server-side user creation.
 * ------------------------------------------------------------------
 */
import { useState, useMemo, useEffect } from 'react';
import {
  UserPlus,
  Users,
  Check,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Download,
  Loader2,
  Send,
  Trash2,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, setDoc, query, where, getCountFromServer } from 'firebase/firestore';
import { functions, db } from '@/lib/firebase';
import { useData } from '@/contexts/DataContext';
import {
  GradientHero,
  SoftCard,
  AccentButton,
  glassFieldClass,
} from '@/components/ui/glass';
import type { Teacher } from '@/types';

interface UserCandidate {
  teacher: Teacher;
  username: string;
  email: string;
  tempPassword: string;
  role: string;
  alreadyExists: boolean;
  status: 'pending' | 'created' | 'failed' | 'skipped';
  error?: string;
}

function generatePassword(): string {
  return 'LKC' + Math.random().toString(36).slice(2, 8).toUpperCase() + '!';
}


export default function BulkUserCreation() {
  const { teachers, users } = useData();
  const [candidates, setCandidates] = useState<UserCandidate[]>([]);
  const [generated, setGenerated] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [roleOverride, setRoleOverride] = useState('subjectTeacher');
  // Email-on-create toggle: OFF = create accounts now, store credentials,
  // and send the welcome emails later (on training day).
  const [emailNow, setEmailNow] = useState(false);
  const [totalStored, setTotalStored] = useState(0);
  const [pendingStored, setPendingStored] = useState(0);
  const [sending, setSending] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  async function refreshStored() {
    try {
      const col = collection(db, 'reportStaffOnboarding');
      const [all, pend] = await Promise.all([
        getCountFromServer(col),
        getCountFromServer(query(col, where('emailed', '==', false))),
      ]);
      setTotalStored(all.data().count);
      setPendingStored(pend.data().count);
    } catch {
      setTotalStored(0);
      setPendingStored(0);
    }
  }
  useEffect(() => { refreshStored(); }, []);

  // Existing user emails for duplicate detection
  const existingEmails = useMemo(
    () => new Set(users.map((u) => (u.email ?? '').toLowerCase())),
    [users],
  );

  function generateCandidates() {
    const results: UserCandidate[] = [];

    for (const t of teachers) {
      // Teachers sign in with their own email on record — no generated
      // usernames. Skip any teacher who has no email.
      const loginEmail = (t.email ?? '').trim().toLowerCase();
      if (!loginEmail) continue;

      const alreadyExists = existingEmails.has(loginEmail);

      results.push({
        teacher: t,
        username: loginEmail.split('@')[0],
        email: loginEmail,
        tempPassword: alreadyExists ? '' : generatePassword(),
        role: t.isClassTeacher ? 'classTeacher' : roleOverride,
        alreadyExists,
        status: alreadyExists ? 'skipped' : 'pending',
      });
    }

    results.sort((a, b) => {
      if (a.alreadyExists !== b.alreadyExists) return a.alreadyExists ? 1 : -1;
      return a.teacher.fullName.localeCompare(b.teacher.fullName);
    });

    setCandidates(results);
    setGenerated(true);
    setMsg(null);
  }

  async function createAll() {
    const toCreate = candidates.filter((c) => c.status === 'pending');
    if (toCreate.length === 0) {
      setMsg({ ok: false, text: 'No pending accounts to create.' });
      return;
    }

    setProcessing(true);
    setMsg(null);
    const fn = httpsCallable(functions, 'createBulkUsers');
    let ok = 0;
    let fail = 0;

    // Process in batches of 5 to avoid overwhelming the server
    for (let i = 0; i < toCreate.length; i += 5) {
      const batch = toCreate.slice(i, i + 5);
      const promises = batch.map(async (c) => {
        try {
          const displayName = `${c.teacher.title} ${c.teacher.fullName}`;
          const res = (await fn({
            email: c.email,
            displayName,
            tempPassword: c.tempPassword,
            role: c.role,
            linkedTeacherId: c.teacher.id,
            // Only email immediately when the toggle is on; otherwise the
            // credential is stored below for a later bulk send.
            notifyEmail: emailNow ? c.email : '',
            teacherName: displayName,
          })) as { data: { ok: boolean; error?: string; uid?: string } };

          if (res.data.ok) {
            ok++;
            // Defer-email mode: stash the credential (admin-only collection)
            // so the welcome email can be sent in bulk later.
            if (!emailNow && res.data.uid) {
              try {
                await setDoc(doc(db, 'reportStaffOnboarding', res.data.uid), {
                  uid: res.data.uid,
                  email: c.email,                 // login email (shown in the message)
                  notifyEmail: c.email, // same as login — the teacher's own email
                  displayName,
                  role: c.role,
                  linkedTeacherId: c.teacher.id,
                  tempPassword: c.tempPassword,
                  emailed: false,
                  createdAt: new Date().toISOString(),
                });
              } catch {
                /* non-fatal: the CSV export still carries the password */
              }
            }
            setCandidates((prev) =>
              prev.map((p) => p.teacher.id === c.teacher.id ? { ...p, status: 'created' } : p),
            );
          } else {
            fail++;
            setCandidates((prev) =>
              prev.map((p) => p.teacher.id === c.teacher.id ? { ...p, status: 'failed', error: res.data.error } : p),
            );
          }
        } catch (e) {
          fail++;
          setCandidates((prev) =>
            prev.map((p) => p.teacher.id === c.teacher.id ? { ...p, status: 'failed', error: (e as Error).message } : p),
          );
        }
      });
      await Promise.all(promises);
    }

    setProcessing(false);
    await refreshStored();
    setMsg({
      ok: fail === 0,
      text: `${ok} accounts created, ${fail} failed.` +
        (!emailNow && ok > 0 ? ' Welcome emails stored — send them from "Stored welcome credentials" when ready.' : ''),
    });
  }

  async function sendPending() {
    setSending(true);
    setMsg(null);
    try {
      const fn = httpsCallable(functions, 'sendPendingWelcomeEmails');
      const res = (await fn({})) as { data: { ok: boolean; sent?: number; failed?: number; reason?: string } };
      if (res.data.ok) {
        setMsg({ ok: (res.data.failed ?? 0) === 0, text: `${res.data.sent ?? 0} welcome email(s) sent, ${res.data.failed ?? 0} failed.` });
      } else {
        setMsg({ ok: false, text: res.data.reason || 'Could not send welcome emails.' });
      }
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSending(false);
      await refreshStored();
    }
  }

  async function discardStored() {
    setDiscarding(true);
    setMsg(null);
    try {
      const fn = httpsCallable(functions, 'discardPendingOnboarding');
      const res = (await fn({})) as { data: { ok: boolean; deleted?: number } };
      setMsg({ ok: true, text: `${res.data.deleted ?? 0} stored credential(s) discarded.` });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setDiscarding(false);
      await refreshStored();
    }
  }

  function exportCsv() {
    const headers = ['Teacher', 'Login Email', 'Password', 'Role', 'Profile Email', 'Status'];
    const rows = candidates.map((c) => [
      `"${c.teacher.title} ${c.teacher.fullName}"`,
      c.email,
      c.tempPassword || '(exists)',
      c.role,
      c.teacher.email || '',
      c.status,
    ].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk_user_accounts_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const pending = candidates.filter((c) => c.status === 'pending').length;
  const created = candidates.filter((c) => c.status === 'created').length;
  const failed = candidates.filter((c) => c.status === 'failed').length;
  const skipped = candidates.filter((c) => c.status === 'skipped').length;

  return (
    <div className="space-y-6">
      <GradientHero
        icon={UserPlus}
        title="Bulk User Creation"
        subtitle="Create login accounts for all teachers at once"
      />

      {msg && (
        <SoftCard>
          <div className={`flex items-center gap-2 text-sm ${msg.ok ? 'text-emerald-700' : 'text-red-700'}`}>
            {msg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />} {msg.text}
          </div>
        </SoftCard>
      )}

      {/* Stored welcome credentials (deferred-email mode) */}
      {totalStored > 0 && (
        <SoftCard>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-1">Stored welcome credentials</h3>
              <p className="text-xs text-gray-500">
                {totalStored} stored · {pendingStored} awaiting email. Temporary passwords are held here until you send the welcome emails, then discard.
              </p>
            </div>
            <div className="flex gap-2">
              <AccentButton
                icon={sending ? Loader2 : Send}
                onClick={sendPending}
                disabled={sending || pendingStored === 0}
              >
                {sending ? 'Sending…' : `Send ${pendingStored} welcome email${pendingStored === 1 ? '' : 's'}`}
              </AccentButton>
              <AccentButton
                variant="soft"
                icon={discarding ? Loader2 : Trash2}
                onClick={discardStored}
                disabled={discarding}
              >
                {discarding ? 'Discarding…' : 'Discard stored credentials'}
              </AccentButton>
            </div>
          </div>
        </SoftCard>
      )}

      {/* Config */}
      <SoftCard>
        <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-3">Configuration</h3>
        <p className="text-sm text-gray-500 mb-4">
          Each teacher signs in with their own email address on record. A temporary password is generated for each.
          With “Email login details now” on, it is emailed to that address immediately; with it off, accounts are created now and the welcome emails are stored to send later.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Default role for non-class teachers</label>
            <select
              value={roleOverride}
              onChange={(e) => setRoleOverride(e.target.value)}
              className={glassFieldClass('sm:w-52')}
            >
              <option value="subjectTeacher">Subject Teacher</option>
              <option value="classTeacher">Class Teacher</option>
              <option value="hoy">Head of Year</option>
              <option value="hod">Head of Department</option>
              <option value="editor">Editor</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">Class teachers are auto-detected from teacher records.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer sm:mt-7">
            <input
              type="checkbox"
              checked={emailNow}
              onChange={(e) => setEmailNow(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Email login details now
          </label>
          <div className="flex gap-2 sm:mt-5">
            <AccentButton icon={Users} onClick={generateCandidates}>
              Generate Accounts
            </AccentButton>
          </div>
        </div>
      </SoftCard>

      {/* Preview table */}
      {generated && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SoftCard pad="p-3"><p className="text-xl font-bold text-gray-800 dark:text-white">{pending}</p><p className="text-xs text-gray-400">Ready</p></SoftCard>
            <SoftCard pad="p-3"><p className="text-xl font-bold text-emerald-600">{created}</p><p className="text-xs text-gray-400">Created</p></SoftCard>
            <SoftCard pad="p-3"><p className="text-xl font-bold text-red-600">{failed}</p><p className="text-xs text-gray-400">Failed</p></SoftCard>
            <SoftCard pad="p-3"><p className="text-xl font-bold text-gray-400">{skipped}</p><p className="text-xs text-gray-400">Already Exist</p></SoftCard>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <AccentButton
              icon={processing ? Loader2 : UserPlus}
              onClick={createAll}
              disabled={processing || pending === 0}
            >
              {processing ? 'Creating…' : `Create ${pending} Accounts`}
            </AccentButton>
            <AccentButton variant="soft" icon={Download} onClick={exportCsv}>
              Export CSV
            </AccentButton>
          </div>

          {/* Table */}
          <SoftCard pad="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-left text-xs text-gray-500">
                    <th className="px-4 py-2.5 font-medium">Teacher</th>
                    <th className="px-4 py-2.5 font-medium">Login Email</th>
                    <th className="px-4 py-2.5 font-medium hidden sm:table-cell">Password</th>
                    <th className="px-4 py-2.5 font-medium hidden md:table-cell">Role</th>
                    <th className="px-4 py-2.5 font-medium hidden md:table-cell">Notify Email</th>
                    <th className="px-3 py-2.5 font-medium text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => (
                    <tr key={c.teacher.id} className={`border-b border-gray-50 dark:border-gray-700/50 ${c.alreadyExists ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-white">
                        {c.teacher.title} {c.teacher.fullName}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                        {c.email}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-600 hidden sm:table-cell">
                        {c.tempPassword || '—'}
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs capitalize hidden md:table-cell">
                        {c.role.replace(/([A-Z])/g, ' $1').trim()}
                      </td>
                      <td className="px-4 py-2.5 text-gray-400 text-xs hidden md:table-cell">
                        {c.teacher.email || <span className="text-amber-500">No email</span>}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {c.status === 'pending' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Ready</span>
                        )}
                        {c.status === 'created' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                            <Check size={12} /> Created
                          </span>
                        )}
                        {c.status === 'failed' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300" title={c.error}>
                            <XCircle size={12} /> Failed
                          </span>
                        )}
                        {c.status === 'skipped' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Exists</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SoftCard>
        </>
      )}
    </div>
  );
}
